import { createSelector, createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { clearState } from './clearSlice';

const channelsAdapter = createEntityAdapter({
   selectId: channel => channel.channelId,
});

// Cache for pre-computed timestamps to avoid repeated Date() calls
const timestampCache = new Map();
const MAX_CACHE_SIZE = 10000;

const getCachedTimestamp = lastActivityAt => {
   if (!timestampCache.has(lastActivityAt)) {
      if (timestampCache.size >= MAX_CACHE_SIZE) {
         const keysToDelete = Array.from(timestampCache.keys()).slice(0, 1000);
         keysToDelete.forEach(key => timestampCache.delete(key));
      }

      timestampCache.set(lastActivityAt, new Date(lastActivityAt).getTime());
   }
   return timestampCache.get(lastActivityAt);
};

const clearTimestampCache = () => {
   timestampCache.clear();
};

// When unpinning, if the unpinned channel has a newer lastActivityAt
// than the current last item in the list, remove it from the array.
// This prevents it from appearing on this page when it belongs earlier
// (helps avoid duplication across paginated pages).
const maybeDropUnpinnedIfAheadOfLast = (state, unpinnedChannelId) => {
   const order = state.channelOrder;
   if (!order || order.length < 2) return;

   let lastId = null;
   for (let i = order.length - 1; i >= 0; i--) {
      if (order[i] !== unpinnedChannelId) {
         lastId = order[i];
         break;
      }
   }
   if (!lastId) return;

   const entities = state.channels.entities;
   const unpinned = entities[unpinnedChannelId];
   const lastItem = entities[lastId];
   if (!unpinned || !lastItem) return;

   const unpinnedTs = getCachedTimestamp(unpinned.lastActivityAt);
   const lastTs = getCachedTimestamp(lastItem.lastActivityAt);

   if (unpinnedTs < lastTs) {
      removeChannelFromOrder(order, unpinnedChannelId);
   }
};

const findInsertionIndex = (channelOrder, targetChannel, channelsEntities, targetTime) => {
   const isPinned = Boolean(targetChannel.pinnedAt && targetChannel.pinnedAt > 0);
   if (channelOrder.length === 0) return 0;

   for (let i = 0; i < channelOrder.length; i++) {
      const otherChannel = channelsEntities[channelOrder[i]];
      if (!otherChannel) continue;

      const otherIsPinned = Boolean(otherChannel.pinnedAt && otherChannel.pinnedAt > 0);

      if (isPinned && !otherIsPinned) {
         return i;
      }

      if (!isPinned && !otherIsPinned) {
         const otherTime = getCachedTimestamp(otherChannel.lastActivityAt);
         if (targetTime > otherTime) {
            return i;
         }
      }

      if (isPinned && otherIsPinned) {
         const otherPinnedTime = otherChannel.pinnedAt || 0;
         if (targetTime > otherPinnedTime) {
            return i;
         }
      }
   }
   return channelOrder.length;
};

const sortChannelsByType = (pinnedChannels, unpinnedChannels) => {
   // Sort pinned channels by pinnedAt timestamp (most recently pinned first)
   pinnedChannels.sort((a, b) => {
      const pinnedTimeA = a.pinnedAt || 0;
      const pinnedTimeB = b.pinnedAt || 0;
      return pinnedTimeB - pinnedTimeA;
   });

   // Sort unpinned channels by lastActivityAt (most recent activity first)
   unpinnedChannels.sort((a, b) => {
      const timeA = getCachedTimestamp(a.lastActivityAt);
      const timeB = getCachedTimestamp(b.lastActivityAt);
      return timeB - timeA;
   });
};

const findChannelBoundary = (channelOrder, channelsEntities) => {
   for (let i = 0; i < channelOrder.length; i++) {
      const channel = channelsEntities[channelOrder[i]];
      if (channel && (!channel.pinnedAt || channel.pinnedAt === 0)) {
         return i;
      }
   }
   return -1;
};

const removeChannelFromOrder = (channelOrder, channelId) => {
   const index = channelOrder.indexOf(channelId);
   if (index !== -1) {
      channelOrder.splice(index, 1);
   }
   return index !== -1;
};

const batchRemoveChannelsFromOrder = (channelOrder, channelIds) => {
   if (channelIds.length === 1) {
      return removeChannelFromOrder(channelOrder, channelIds[0]);
   }

   const toRemove = new Set(channelIds);
   let writeIndex = 0;

   for (const channelId of channelOrder) {
      if (!toRemove.has(channelId)) {
         channelOrder[writeIndex] = channelId;
         writeIndex++;
      }
   }

   channelOrder.length = writeIndex;
   return channelIds.length;
};

const insertChannelAtPosition = (channelOrder, channelId, channelsEntities, channel) => {
   const targetTime =
      channel.pinnedAt && channel.pinnedAt > 0 ? channel.pinnedAt : getCachedTimestamp(channel.lastActivityAt);

   const insertIndex = findInsertionIndex(channelOrder, channel, channelsEntities, targetTime);
   channelOrder.splice(insertIndex, 0, channelId);
};

const initialState = {
   channels: channelsAdapter.getInitialState(),
   channelOrder: [],
   loading: false,
   error: null,
   page: 1,
   hasMore: true,
   searchText: '',
   searchTextByScope: {},
   unreadRecords: 0,
};

const recentChannelListSlice = createSlice({
   name: 'recentChannelListData',
   initialState,
   reducers: {
      appendRecentChannels(state, action) {
         const channels = action.payload;
         if (!channels.length) return;

         channelsAdapter.upsertMany(state.channels, channels);
         const existingChannelIds = new Set(state.channelOrder);

         const newPinnedChannels = [];
         const newUnpinnedChannels = [];

         for (const channel of channels) {
            if (existingChannelIds.has(channel.channelId)) continue;

            if (channel.pinnedAt > 0) {
               newPinnedChannels.push(channel);
            } else {
               newUnpinnedChannels.push(channel);
            }
         }

         if (newPinnedChannels.length === 0 && newUnpinnedChannels.length === 0) {
            return;
         }
         sortChannelsByType(newPinnedChannels, newUnpinnedChannels);

         if (newPinnedChannels.length > 0) {
            const boundary = findChannelBoundary(state.channelOrder, state.channels.entities);
            const newPinnedIds = newPinnedChannels.map(channel => channel.channelId);
            const insertPosition = boundary === -1 ? 0 : boundary;
            state.channelOrder.splice(insertPosition, 0, ...newPinnedIds);
         }

         if (newUnpinnedChannels.length > 0) {
            const newUnpinnedIds = newUnpinnedChannels.map(channel => channel.channelId);
            state.channelOrder.push(...newUnpinnedIds);
         }
      },

      upsertRecentChannelItem(state, action) {
         const { channelId, lastActivityAt = new Date().toISOString(), pinnedAt } = action.payload;

         const existingChannel = state.channels.entities[channelId];
         const currentTime = Date.now();

         const resolvedPinnedAt = pinnedAt !== undefined ? pinnedAt : existingChannel?.pinnedAt || 0;

         const channelData = {
            channelId,
            lastActivityAt,
            lastUpdated: currentTime,
            pinnedAt: resolvedPinnedAt,
         };

         if (existingChannel) {
            if (existingChannel.lastActivityAt > lastActivityAt) return;

            channelsAdapter.updateOne(state.channels, {
               id: channelId,
               changes: channelData,
            });
         } else {
            channelsAdapter.addOne(state.channels, channelData);
         }

         const updatedChannel = state.channels.entities[channelId];

         removeChannelFromOrder(state.channelOrder, channelId);
         insertChannelAtPosition(state.channelOrder, channelId, state.channels.entities, updatedChannel);
      },

      deleteRecentChannelItem(state, action) {
         const { channelId } = action.payload;
         channelsAdapter.removeOne(state.channels, channelId);
         removeChannelFromOrder(state.channelOrder, channelId);
      },

      deleteMultipleRecentChannels(state, action) {
         const { channelIds } = action.payload;
         if (!Array.isArray(channelIds) || channelIds.length === 0) return;

         channelsAdapter.removeMany(state.channels, channelIds);

         const idsToRemove = new Set(channelIds);
         const newOrder = [];

         for (const channelId of state.channelOrder) {
            if (!idsToRemove.has(channelId)) {
               newOrder.push(channelId);
            }
         }

         state.channelOrder = newOrder;
      },

      batchSetRecentChannelPin(state, action) {
         const { chats = [], action: pinAction = 0 } = action.payload;
         if (!Array.isArray(chats) || chats.length === 0) return;

         const shouldPinAll = pinAction === 1;
         const channelsNeedingReorder = [];
         const unpinnedIds = [];

         for (const { channelId, pinTime = 0 } of chats) {
            const channel = state.channels.entities[channelId];
            if (!channel) continue;

            const wasPinned = Boolean(channel.pinnedAt && channel.pinnedAt > 0);
            const newPinnedAt = shouldPinAll ? pinTime : 0;

            channel.pinnedAt = newPinnedAt;

            const pinStateChanged = wasPinned !== shouldPinAll;
            const pinTimestampProvided = shouldPinAll && pinTime;

            if (pinStateChanged || pinTimestampProvided) {
               channelsNeedingReorder.push(channel);
            }

            if (wasPinned && !shouldPinAll) {
               unpinnedIds.push(channelId);
            }
         }

         if (channelsNeedingReorder.length === 0) return;

         const channelIdsToRemove = channelsNeedingReorder.map(ch => ch.channelId);
         batchRemoveChannelsFromOrder(state.channelOrder, channelIdsToRemove);

         // Insert back in correct positions
         for (const channel of channelsNeedingReorder) {
            insertChannelAtPosition(state.channelOrder, channel.channelId, state.channels.entities, channel);
         }

         if (!shouldPinAll && unpinnedIds.length > 0) {
            for (const unpinnedId of unpinnedIds) {
               maybeDropUnpinnedIfAheadOfLast(state, unpinnedId);
            }
         }
      },

      setRecentChannelLoading(state, action) {
         state.loading = action.payload;
      },

      setRecentChannelHasMore(state, action) {
         state.hasMore = action.payload;
      },

      setSearchTextByScope(state, action) {
         const { scopeKey, searchText } = action.payload;
         state.searchTextByScope[scopeKey] = searchText;
      },
      setRecentChannelUnreadRecords(state, action) {
         state.unreadRecords = action.payload;
      },
      setRecentChannels(state, action) {
         const { channels, unreadRecords = 0, } = action.payload;

         // Clear existing data
         channelsAdapter.removeAll(state.channels);
         state.channelOrder = [];

         // Add new channels if provided
         if (channels && channels.length > 0) {
            channelsAdapter.setAll(state.channels, channels);

            const pinnedChannels = [];
            const unpinnedChannels = [];

            for (const channel of channels) {
               if (channel.pinnedAt > 0) {
                  pinnedChannels.push(channel);
               } else {
                  unpinnedChannels.push(channel);
               }
            }

            sortChannelsByType(pinnedChannels, unpinnedChannels);

            const pinnedIds = pinnedChannels.map(channel => channel.channelId);
            const unpinnedIds = unpinnedChannels.map(channel => channel.channelId);

            state.channelOrder = [...pinnedIds, ...unpinnedIds];
         }

         // Update metadata
         state.unreadRecords = unreadRecords;
         // Use pagination metadata if available, otherwise fallback to checking if channels exist
         state.hasMore = true;
         state.loading = false;
      },
   },
   extraReducers: builder => {
      builder.addCase(clearState, () => {
         clearTimestampCache();
         return initialState;
      });
   },
});

export const {
   batchSetRecentChannelPin,
   appendRecentChannels,
   setRecentChannelLoading,
   setRecentChannelHasMore,
   upsertRecentChannelItem,
   setSearchTextByScope,
   deleteRecentChannelItem,
   deleteMultipleRecentChannels,
   setRecentChannelUnreadRecords,
   setRecentChannels,
} = recentChannelListSlice.actions;

export default recentChannelListSlice.reducer;

export const {
   selectAll: selectAllRecentChannels,
   selectById: selectChannelById,
   selectIds: selectChannelIds,
   selectEntities: selectChannelEntities,
   selectTotal: selectTotalChannels,
} = channelsAdapter.getSelectors(state => state.recentChannelListData.channels);

const selectChannelOrder = state => state.recentChannelListData.channelOrder;
const selectChannelEntitiesFromState = state => state.recentChannelListData.channels.entities;

export const selectOrderedRecentChannels = createSelector(
   [selectChannelOrder, selectChannelEntitiesFromState],
   (channelOrder, entities) => {
      const result = new Array(channelOrder.length);
      let writeIndex = 0;

      for (const channelId of channelOrder) {
         const channel = entities[channelId];
         if (channel) {
            result[writeIndex++] = channel;
         }
      }

      result.length = writeIndex;
      return result;
   },
);

export const selectChannelInfo = state => state.channelInfoData.channelInfo;

export const selectOrderedChannels = createSelector(
   [selectOrderedRecentChannels, selectChannelInfo],
   (recentChannels, channelInfo) => {
      const result = new Array(recentChannels.length);

      for (let i = 0; i < recentChannels.length; i++) {
         const recentChannel = recentChannels[i];
         const info = channelInfo[recentChannel.channelId];
         result[i] = {
            channelId: recentChannel.channelId,
            lastActivityAt: recentChannel.lastActivityAt,
            pinnedAt: recentChannel.pinnedAt,
            name: info?.name || 'Unknown Channel',
         };
      }

      return result;
   },
);

export const selectChannelsBySearch = createSelector(
   [selectOrderedChannels, state => state.recentChannelListData.searchText],
   (allChannels, searchText) => {
      if (!searchText) return allChannels;

      const lowerSearchText = searchText.toLowerCase();
      const result = [];

      for (const channel of allChannels) {
         if (
            channel.name.toLowerCase().includes(lowerSearchText) ||
            channel.description?.toLowerCase().includes(lowerSearchText)
         ) {
            result.push(channel);
         }
      }

      return result;
   },
);

export const selectRecentChatData = selectOrderedRecentChannels;
export const selectSearchText = state => state.recentChannelListData.searchText;
export const selectLoading = state => state.recentChannelListData.loading;
export const selectHasMore = state => state.recentChannelListData.hasMore;

export const selectSearchTextByScope = scopeKey => state =>
   state.recentChannelListData.searchTextByScope[scopeKey] || '';

export const selectFilteredRecentChatData = scopeKey =>
   createSelector([selectOrderedChannels, selectSearchTextByScope(scopeKey)], (channels, searchText) => {
      const trimmedText = searchText?.trim();

      if (!trimmedText) {
         return channels;
      }

      const lowerSearchText = trimmedText.toLowerCase();
      const result = [];

      for (const channel of channels) {
         if (
            channel.name.toLowerCase().includes(lowerSearchText) ||
            channel.description?.toLowerCase().includes(lowerSearchText)
         ) {
            result.push(channel);
         }
      }

      return result;
   });

export const selectChannelsData = createSelector(
   [selectChannelsBySearch, state => state.recentChannelListData],
   (channels, recentChannelsState) => ({
      channels,
      loading: recentChannelsState.loading,
      hasMore: recentChannelsState.hasMore,
      searchText: recentChannelsState.searchText,
   }),
);
